import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ImageOff, Upload } from 'lucide-react';
import { cn, errorHandler } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { StringHelper } from '@/helpers/string.helper';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { FileDropZone } from '@/components/file-drop-zone';
import { useMutation } from '@tanstack/react-query';
import { uploadCampaignImage } from '@/services/campaign';
import { toast } from 'sonner';
export const CampaignImageCard = ({ campaign, className, onSuccess }) => {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const [images, setImages] = React.useState([]);
    const { mutate: upload, isPending: isUploading } = useMutation({
        mutationFn: uploadCampaignImage,
        onSuccess: (data) => {
            toast.success(data.message);
            onSuccess?.(data.campaign);
            setOpen(false);
        },
        onError: errorHandler,
    });
    const heroSrc = campaign.imageDetailUrl ?? campaign.imageUrl;
    const hasImage = Boolean(heroSrc);
    const statusLabel = t(`campaigns.status.${campaign.status}`, { ns: 'glossary' }) ?? campaign.status;
    const handleDrop = (acceptedFiles) => {
        setImages((prev) => [...prev, ...acceptedFiles]);
    };
    const handleInputUpload = (e) => {
        const files = e.target.files;
        if (!files)
            return;
        setImages((prev) => [...prev, ...Array.from(files)]);
    };
    const handleRemove = (index) => {
        setImages((prev) => prev.filter((_, i) => i !== index));
    };
    const handleConfirmUpload = async () => {
        if (!images.length)
            return;
        upload({ publicId: campaign.publicId, imageFile: images[0] });
    };
    return (<Card className={cn('w-full overflow-hidden rounded-xl shadow-sm', className)}>
            <div className="relative h-65 bg-gray-100 flex items-center justify-center">
                {hasImage ? (<img src={heroSrc} alt={campaign.name} className="h-full w-full object-contain" loading="lazy"/>) : (<div className="flex flex-col items-center justify-center text-gray-400">
                        <ImageOff size={40}/>
                        <p className="text-sm mt-2">{t('campaigns.no_image', { defaultValue: 'No campaign image' })}</p>
                    </div>)}

                <div className={cn('absolute top-3 left-3 px-3 py-1 text-xs font-semibold rounded-full text-white', StringHelper.getStatusBgStyles(campaign.status))}>
                    {statusLabel}
                </div>
            </div>

            <CardContent className="p-3">
                <Dialog open={open} onOpenChange={(open) => {
            setOpen(open);
            if (open) {
                setImages([]);
            }
        }}>
                    <DialogTrigger asChild>
                        <Button className="w-full" variant="outline" size="sm">
                            <Upload className="h-4 w-4 mr-2"/>
                            {t('campaigns.upload_image', { ns: 'glossary' })}
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Upload campaign image</DialogTitle>
                        </DialogHeader>

                        <FileDropZone images={images} onDrop={handleDrop} onUpload={handleInputUpload} onRemove={handleRemove}/>

                        <div className="flex justify-end gap-2 mt-4">
                            <Button disabled={isUploading} variant="ghost" onClick={() => setOpen(false)}>
                                Cancel
                            </Button>
                            <Button onClick={handleConfirmUpload} disabled={!images.length || isUploading}>
                                {isUploading ? 'Uploading...' : 'Upload'}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </CardContent>
        </Card>);
};
CampaignImageCard.displayName = 'CampaignImageCard';
